import { create } from 'zustand'
import { McpTool, Tag } from '../types/types'
import { tagsApi } from '../lib/apiClient'
import { useToolsStore } from './toolsStore'

interface TagsState {
  tags: Tag[]
  setTags: (tags: Tag[]) => void
  getTags: () => Promise<any>
  addTag: (tag: Tag) => void
  refreshTagsFromTools: (tools: McpTool[]) => void
  removeTag: (id: number) => void
  createTag: (name: string) => Promise<void>
  deleteTag: (id: number) => Promise<void>
  renameTag: (id: number, newName: string) => Promise<void>
  lastRenamed?: { id?: number, oldName: string, newName: string, ts: number }
}

export const useTagsStore = create<TagsState>((set, get) => ({
  tags: [],
  getTags: () => tagsApi.listWithCount(),
  setTags: (tags) => set({ tags }),
  addTag: (tag) => {
    const trimmed = tag.name.trim().toLowerCase()
    if (!trimmed) return
    const current = get().tags
    if (!current.some(t => t.name === trimmed)) {
      set({ tags: [...current, { id: tag.id, name: trimmed, count: tag.count ?? 0 }].sort((a,b) => a.name.localeCompare(b.name)) })
    }
  },
  removeTag: (id) => {
    set({ tags: get().tags.filter(t => t.id !== id) })
  },
  createTag: async (name: string) => {
    const trimmed = name.trim().toLowerCase()
    if (!trimmed) return
    try {
      const created = await tagsApi.create(trimmed)
      // created might be { id, name } or just return name; normalize
      const id = created?.id
      const tagName = created?.name || trimmed
      const current = get().tags
      if (!current.some(t => t.name === tagName)) {
        set({ tags: [...current, { id, name: tagName, count: 0 }].sort((a,b) => a.name.localeCompare(b.name)) })
      }
    } catch (e) {
      // Surface error upstream; swallow here
      throw e
    }
  },
  deleteTag: async (id: number) => {
    try {
      await tagsApi.delete(id)
      get().removeTag(id)
    } catch (e) {
      throw e
    }
  },
  renameTag: async (id: number, newName: string) => {
    // Utilize new backend rename endpoint (PUT /tags/:id { name })
    const trimmed = newName.trim().toLowerCase()
    if (!trimmed) return
    const existing = get().tags.find(t => t.id === id)
    if (!existing || existing.name === trimmed) return
    try {
      const updated = await tagsApi.rename(id, trimmed)
      const tagName = updated?.name || trimmed
      const oldName = existing.name
      set({ 
        tags: get().tags.map(t => t.id === id ? { ...t, name: tagName } : t).sort((a,b) => a.name.localeCompare(b.name)),
        lastRenamed: { id, oldName, newName: tagName, ts: Date.now() }
      })
      // propagate to tools store
      useToolsStore.getState().renameTagInTools(id, oldName, tagName)
    } catch (e) {
      throw e
    }
  },
  refreshTagsFromTools: (tools) => {
    // Preserve existing tags (object shape) and merge new names from tools.
    const existingObjects = get().tags
    const existingNames = new Set(existingObjects.map(t => t.name))
    tools.forEach(tool => {
      if (Array.isArray(tool.tags)) {
        tool.tags.forEach(tagObj => {
          const name = (tagObj as any).name ? String((tagObj as any).name) : String(tagObj)
          const normalized = name.trim().toLowerCase()
            if (normalized && !existingNames.has(normalized)) {
              existingNames.add(normalized)
            }
        })
      }
    })
    const merged = Array.from(existingNames)
      .sort((a,b) => a.localeCompare(b))
      .map(name => {
        const found = existingObjects.find(t => t.name === name)
        return found ? found : { id: undefined, name, count: 0 }
      })
    set({ tags: merged })
  }
}))
